/**
Author - Vikrant Singh
Git - git@github.com:vikrantSingh97
**/

import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import {ActionButton} from 'screens/commons';
import IMAGES from 'constants/Images';

const propTypes = {
  partnerName: PropTypes.string,
  agreementUrl: PropTypes.string.isRequired,
  onDownload: PropTypes.func,
  buttonText: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  infoHeading: PropTypes.string,
  infoDescription: PropTypes.string,
};

const DocumentPreviewCard = ({
  partnerName,
  agreementUrl,
  onDownload,
  buttonText = 'View Agreement',
  title = 'Partnership Agreement',
  subtitle,
  infoHeading = 'Review Your Agreement',
  infoDescription = 'Please download and review your partnership agreement carefully before proceeding with the digital signature.',
}) => {
  const defaultSubtitle = partnerName
    ? `${partnerName} Partnership Agreement`
    : 'Wealthy Partnership Agreement';

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else {
      const pdfName = partnerName
        ? `${partnerName.toLowerCase().replace(' ', '_')}_agreement.pdf`
        : 'wealthy_partner_agreement.pdf';

      const link = document.createElement('a');
      link.href = agreementUrl;
      link.download = pdfName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <DocumentPreviewCardContainer>
      <DocumentHeader>
        <DocumentIconContainer>
          <DocumentIcon src={IMAGES.fileIcon} alt="Document" />
        </DocumentIconContainer>
        <DocumentHeaderContent>
          <DocumentTitle>{title}</DocumentTitle>
          <DocumentSubtitle>{subtitle || defaultSubtitle}</DocumentSubtitle>
        </DocumentHeaderContent>
      </DocumentHeader>

      <DocumentInfoSection>
        <InfoCard>
          <InfoIconContainer>
            <InfoIcon src={IMAGES.infoIcon} alt="Info" />
          </InfoIconContainer>
          <InfoContent>
            <InfoHeading>{infoHeading}</InfoHeading>
            <InfoDescription>{infoDescription}</InfoDescription>
          </InfoContent>
        </InfoCard>

        <DocumentDetails>
          <DetailItem>
            <DetailIcon src={IMAGES.lockIcon} alt="Secure" />
            <DetailText>Secure document download</DetailText>
          </DetailItem>
          <DetailItem>
            <DetailIcon src={IMAGES.greenTickFilled} alt="Verified" />
            <DetailText>Legally binding agreement</DetailText>
          </DetailItem>
          <DetailItem>
            <DetailIcon src={IMAGES.clockIcon} alt="Time" />
            <DetailText>Valid for immediate signing</DetailText>
          </DetailItem>
        </DocumentDetails>
      </DocumentInfoSection>

      <DownloadButtonContainer>
        <ActionButton
          buttonText={buttonText}
          callback={handleDownload}
          additionalStyles={{
            background: 'transparent',
            border: `2px solid #6725F4`,
            color: '#6725F4',
            fontWeight: '600',
          }}
          spinnerColor="#6725F4"
        />
      </DownloadButtonContainer>
    </DocumentPreviewCardContainer>
  );
};

// Enhanced Document Preview Components
const DocumentPreviewCardContainer = styled.div`
  background: ${(props) => props.theme.colors.background.primary};
  border-radius: 12px;
  border: 1px solid ${(props) => props.theme.colors.border.default};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  flex: 1;
  height: max-content;
  display: flex;
  flex-direction: column;
`;

const DocumentHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  background: ${(props) => props.theme.colors.brand.secondary};
  border-bottom: 1px solid ${(props) => props.theme.colors.border.default};
`;

const DocumentIconContainer = styled.div`
  width: 48px;
  height: 48px;
  background: ${(props) => props.theme.colors.brand.primary};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  flex-shrink: 0;
`;

const DocumentIcon = styled.img`
  width: 24px;
  height: 24px;
  filter: brightness(0) invert(1);
`;

const DocumentHeaderContent = styled.div`
  flex: 1;
`;

const DocumentTitle = styled.h3`
  margin: 0 0 0.25rem 0;
  color: ${(props) => props.theme.colors.text.primary};
  font-size: ${(props) => props.theme.typography.fontSize.lg};
  font-weight: ${(props) => props.theme.typography.fontWeight.semiBold};
  line-height: ${(props) => props.theme.typography.lineHeight.tight};
`;

const DocumentSubtitle = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.text.secondary};
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  line-height: ${(props) => props.theme.typography.lineHeight.normal};
  text-transform: capitalize !important;
`;

const DocumentInfoSection = styled.div`
  flex: 1;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InfoCard = styled.div`
  background: rgba(103, 37, 244, 0.05);
  border: 1px solid rgba(103, 37, 244, 0.1);
  border-radius: 8px;
  padding: 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const InfoIconContainer = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const InfoIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoHeading = styled.h4`
  margin: 0 0 0.5rem 0;
  color: ${(props) => props.theme.colors.text.primary};
  font-size: ${(props) => props.theme.typography.fontSize.md};
  font-weight: ${(props) => props.theme.typography.fontWeight.semiBold};
  line-height: ${(props) => props.theme.typography.lineHeight.tight};
`;

const InfoDescription = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.text.secondary};
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  line-height: ${(props) => props.theme.typography.lineHeight.normal};
`;

const DocumentDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const DetailIcon = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const DetailText = styled.span`
  color: ${(props) => props.theme.colors.text.primary};
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  line-height: ${(props) => props.theme.typography.lineHeight.normal};
`;

const DownloadButtonContainer = styled.div`
  padding: 1.5rem;
  border-top: 1px solid ${(props) => props.theme.colors.border.default};
  background: ${(props) => props.theme.colors.background.primary};
`;

DocumentPreviewCard.propTypes = propTypes;

export default DocumentPreviewCard;
